// 人才系统数据

// 流派定义
export type TalentSchool = 
  | 'confucian'   // 儒家 - 政治、民心
  | 'legalist'    // 法家 - 政治、治安
  | 'mohist'      // 墨家 - 技术、防御
  | 'taoist'      // 道家 - 智谋、外交
  | 'military'    // 兵家 - 军事、战略
  | 'diplomat'    // 纵横家 - 外交、谋略
  | 'merchant'    // 商家 - 商业、经济
  | 'farmer';     // 农家 - 农业、民生

export interface SchoolDefinition {
  id: TalentSchool;
  name: string;
  description: string;
  color: string;
  bonuses: {
    politics?: number;
    military?: number;
    intelligence?: number;
    charisma?: number;
  };
  specialAbility: string;
}

export const SCHOOLS: SchoolDefinition[] = [
  {
    id: 'confucian',
    name: '儒家',
    description: '重视仁义礼智，善于治理民政',
    color: '#c93756',
    bonuses: { politics: 10, charisma: 5 },
    specialAbility: '仁政：提升民心增长速度',
  },
  {
    id: 'legalist',
    name: '法家',
    description: '以法治国，重刑赏',
    color: '#2c3e50',
    bonuses: { politics: 10, intelligence: 5 },
    specialAbility: '严刑：提升治安，降低叛乱概率',
  },
  {
    id: 'mohist',
    name: '墨家',
    description: '兼爱非攻，善守御',
    color: '#5d4e37',
    bonuses: { military: 5, intelligence: 10 },
    specialAbility: '守御：城池防御力提升',
  },
  {
    id: 'taoist',
    name: '道家',
    description: '无为而治，深谋远虑',
    color: '#4a7c59',
    bonuses: { intelligence: 10, charisma: 5 },
    specialAbility: '无为：减少内政消耗',
  },
  {
    id: 'military',
    name: '兵家',
    description: '精通兵法，善战',
    color: '#8b0000',
    bonuses: { military: 15 },
    specialAbility: '兵法：军队战斗力大幅提升',
  },
  {
    id: 'diplomat',
    name: '纵横家',
    description: '善于辞令，游说诸侯',
    color: '#d4af37',
    bonuses: { intelligence: 5, charisma: 10 },
    specialAbility: '连横：外交成功率提升',
  },
  {
    id: 'merchant',
    name: '商家',
    description: '精于计算，善经商',
    color: '#b87333',
    bonuses: { intelligence: 5, charisma: 5 },
    specialAbility: '经商：商业税收增加',
  },
  {
    id: 'farmer',
    name: '农家',
    description: '重视农耕，劝课农桑',
    color: '#6b8e6b',
    bonuses: { politics: 5, charisma: 5 },
    specialAbility: '劝农：粮食产量提升',
  },
];

// 姓氏库（春秋战国时期常见姓氏）
const SURNAMES = [
  '姬', '姜', '子', '嬴', '芈', '妫', '姒', '姚',
  '赵', '钱', '孙', '李', '周', '吴', '郑', '王',
  '冯', '陈', '褚', '卫', '蒋', '沈', '韩', '杨',
  '孔', '曹', '严', '华', '金', '魏', '陶', '姜',
  '戚', '谢', '邹', '喻', '柏', '水', '窦', '章',
  '云', '苏', '潘', '葛', '奚', '范', '彭', '郎',
  '鲁', '韦', '昌', '马', '苗', '凤', '花', '方',
  '俞', '任', '袁', '柳', '酆', '鲍', '史', '唐',
];

// 名字库（春秋战国时期常见名字）
const NAMES = [
  '文', '武', '平', '安', '成', '康', '景', '庄',
  '襄', '昭', '穆', '宣', '惠', '简', '灵', '悼',
  '元', '哀', '怀', '顷', '定', '隐', '桓', '僖',
  '子', '孙', '伯', '仲', '叔', '季', '孟', '少',
  '公', '侯', '君', '卿', '宰', '尹', '史', '卜',
  '生', '产', '驹', '禽', '虎', '豹', '狼', '熊',
  '羽', '翼', '飞', '翔', '游', '遨', '翔', '翥',
  '明', '亮', '光', '辉', '耀', '灿', '煜', '炜',
];

// 字号库
const STYLE_NAMES = [
  '子', '伯', '仲', '叔', '季', '孟', '少', '长',
];

// 随机生成名字
export function generateRandomName(): { surname: string; name: string; fullName: string } {
  const surname = SURNAMES[Math.floor(Math.random() * SURNAMES.length)];
  const name = NAMES[Math.floor(Math.random() * NAMES.length)];
  
  // 有一定概率有双字名
  if (Math.random() > 0.7) {
    const name2 = NAMES[Math.floor(Math.random() * NAMES.length)];
    return { surname, name: name + name2, fullName: surname + name + name2 };
  }
  
  return { surname, name, fullName: surname + name };
}

// 人才品级（参考古代九品中正制及人才评价体系）
export type TalentQuality = 'common' | 'rare' | 'epic' | 'legendary';

export interface QualityDefinition {
  id: TalentQuality;
  name: string;
  description: string;
  color: string;
  minStats: number;
  maxStats: number;
  probability: number;
}

export const QUALITIES: QualityDefinition[] = [
  { 
    id: 'common', 
    name: '凡才', 
    description: '才学平平，可堪一用',
    color: '#888888', 
    minStats: 20, 
    maxStats: 50, 
    probability: 0.5 
  },
  { 
    id: 'rare', 
    name: '良才', 
    description: '才学出众，堪当大任',
    color: '#4a90e2', 
    minStats: 40, 
    maxStats: 70, 
    probability: 0.3 
  },
  { 
    id: 'epic', 
    name: '国士', 
    description: '国士无双，栋梁之材',
    color: '#9b59b6', 
    minStats: 60, 
    maxStats: 85, 
    probability: 0.15 
  },
  { 
    id: 'legendary', 
    name: '圣贤', 
    description: '经天纬地，千古流芳',
    color: '#d4af37', 
    minStats: 80, 
    maxStats: 100, 
    probability: 0.05 
  },
];

// 人才接口
export interface Talent {
  id: string;
  name: string;
  surname: string;
  fullName: string;
  styleName?: string;
  quality: TalentQuality;
  school: TalentSchool;
  era: string;
  
  // 基础属性
  politics: number;      // 政治
  military: number;      // 军事
  intelligence: number;  // 智谋
  charisma: number;      // 魅力
  
  // 成长属性
  level: number;
  experience: number;
  
  // 状态
  loyalty: number;
  position: string | null;
  
  // 描述
  description: string;
  
  // 是否正在进修
  isTraining: boolean;
  trainingEndTime?: number;
  
  // 历史人物标记（可选）
  isHistorical?: boolean;
  historicalId?: string;
}

// 生成随机人才
export function generateRandomTalent(era: string = '春秋'): Talent {
  const id = `talent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const nameData = generateRandomName();
  
  // 随机品质
  const rand = Math.random();
  let quality: TalentQuality = 'common';
  let cumulative = 0;
  for (const q of QUALITIES) {
    cumulative += q.probability;
    if (rand <= cumulative) {
      quality = q.id;
      break;
    }
  }
  
  // 随机流派
  const school = SCHOOLS[Math.floor(Math.random() * SCHOOLS.length)].id;
  const schoolDef = SCHOOLS.find(s => s.id === school)!;
  
  // 根据品质生成属性
  const qualityDef = QUALITIES.find(q => q.id === quality)!;
  const baseStat = () => Math.floor(Math.random() * (qualityDef.maxStats - qualityDef.minStats) + qualityDef.minStats);
  
  let politics = baseStat();
  let military = baseStat();
  let intelligence = baseStat();
  let charisma = baseStat();
  
  // 应用流派加成
  if (schoolDef.bonuses.politics) politics += schoolDef.bonuses.politics;
  if (schoolDef.bonuses.military) military += schoolDef.bonuses.military;
  if (schoolDef.bonuses.intelligence) intelligence += schoolDef.bonuses.intelligence;
  if (schoolDef.bonuses.charisma) charisma += schoolDef.bonuses.charisma;
  
  // 确保不超过100
  politics = Math.min(100, politics);
  military = Math.min(100, military);
  intelligence = Math.min(100, intelligence);
  charisma = Math.min(100, charisma);
  
  // 生成描述
  const descriptions = [
    `出身${schoolDef.name}，${schoolDef.description}`,
    `精通${schoolDef.name}之学，有${schoolDef.specialAbility}之能`,
    `师从${schoolDef.name}大师，深谙${schoolDef.name.substring(0, 1)}学精髓`,
  ];
  
  return {
    id,
    name: nameData.name,
    surname: nameData.surname,
    fullName: nameData.fullName,
    styleName: Math.random() > 0.5 ? STYLE_NAMES[Math.floor(Math.random() * STYLE_NAMES.length)] : undefined,
    quality,
    school,
    era,
    politics,
    military,
    intelligence,
    charisma,
    level: 1,
    experience: 0,
    loyalty: 70 + Math.floor(Math.random() * 20),
    position: null,
    description: descriptions[Math.floor(Math.random() * descriptions.length)],
    isTraining: false,
  };
}

// 生成多个随机人才
export function generateRandomTalents(count: number, era?: string): Talent[] {
  return Array.from({ length: count }, () => generateRandomTalent(era));
}

// 进修选项
export interface TrainingOption {
  id: string;
  name: string;
  description: string;
  cost: {
    money: number;
    prestige: number;
  };
  duration: number; // 月数
  effects: {
    statBoost?: { stat: keyof Talent; value: number };
    schoolChange?: TalentSchool;
    expGain?: number;
  };
}

export const TRAINING_OPTIONS: TrainingOption[] = [
  {
    id: 'politics_training',
    name: '政治研修',
    description: '研习治国之道，提升政治能力',
    cost: { money: 200, prestige: 5 },
    duration: 3,
    effects: { statBoost: { stat: 'politics', value: 5 }, expGain: 100 },
  },
  {
    id: 'military_training',
    name: '兵法研习',
    description: '研读兵书战策，提升军事才能',
    cost: { money: 200, prestige: 5 },
    duration: 3,
    effects: { statBoost: { stat: 'military', value: 5 }, expGain: 100 },
  },
  {
    id: 'intelligence_training',
    name: '谋略研修',
    description: '学习纵横之术，提升智谋',
    cost: { money: 200, prestige: 5 },
    duration: 3,
    effects: { statBoost: { stat: 'intelligence', value: 5 }, expGain: 100 },
  },
  {
    id: 'charisma_training',
    name: '德行修养',
    description: '修身养性，提升个人魅力',
    cost: { money: 200, prestige: 5 },
    duration: 3,
    effects: { statBoost: { stat: 'charisma', value: 5 }, expGain: 100 },
  },
  {
    id: 'school_change_confucian',
    name: '转修儒家',
    description: '弃旧学新，改修儒家之道',
    cost: { money: 500, prestige: 20 },
    duration: 6,
    effects: { schoolChange: 'confucian', expGain: 200 },
  },
  {
    id: 'school_change_legalist',
    name: '转修法家',
    description: '弃旧学新，改修法家之术',
    cost: { money: 500, prestige: 20 },
    duration: 6,
    effects: { schoolChange: 'legalist', expGain: 200 },
  },
  {
    id: 'school_change_military',
    name: '转修兵家',
    description: '弃旧学新，改修兵家之法',
    cost: { money: 500, prestige: 20 },
    duration: 6,
    effects: { schoolChange: 'military', expGain: 200 },
  },
  {
    id: 'school_change_taoist',
    name: '转修道家',
    description: '弃旧学新，改修道家之学',
    cost: { money: 500, prestige: 20 },
    duration: 6,
    effects: { schoolChange: 'taoist', expGain: 200 },
  },
];
